import { useCallback, useEffect, useRef, useState } from 'react'
import { IoPhonePortraitOutline } from 'react-icons/io5'
import { CloseIcon } from '@/components/icons/baba-icons'
import QrCode from './QrCode'
import { AppStoreLinks } from './StoreBadges'
import {
  formatCountdown,
  formatLinkCode,
  phoneLinkUrl,
  secondsUntilExpiry,
} from '@/lib/phoneLink'
import { useCreateAccountLinkCodeMutation } from '../store/babaApi'

/**
 * Automatic regenerations per mount. A link code lives ten minutes; a tab left
 * open overnight would otherwise sit in a refresh loop against the API, so
 * after this many silent renewals the card asks before minting another.
 */
const MAX_AUTO_RENEWALS = 3

/**
 * "Use baba on Your Phone" — a QR plus the same code in readable form.
 *
 * Scanning is the fast path: the phone's camera opens
 * app.itsbaba.com/link?code=…, which hands the code to the installed app (or
 * to the store, if there is no app yet). Typing the eight characters into
 * Settings › Sign in is the fallback for anyone whose camera app is unhelpful,
 * so both routes are always on screen — the QR is an accelerator, never the
 * only way through.
 *
 * Only the phone stores appear here, never the Chrome extension: this card is
 * answering "how do I get this on my phone", and a browser extension is a
 * different question asked somewhere else.
 */
export default function PhoneLinkCard({
  placement,
  variant = 'plain',
  onDismiss,
}: {
  /** Attribution for the store-badge links, e.g. 'settings_account_sync'. */
  placement: string
  /** 'plain' sits inside an existing card; 'panel' is the slim standalone strip. */
  variant?: 'plain' | 'panel'
  /** Rendering a dismiss control is what makes this the one-time panel. */
  onDismiss?: () => void
}) {
  const [createLinkCode, { data: linkCode, isLoading, error, reset }] = useCreateAccountLinkCodeMutation()
  const [secondsLeft, setSecondsLeft] = useState(0)
  const [expired, setExpired] = useState(false)
  const renewalsRef = useRef(0)
  const requestedRef = useRef(false)

  const requestCode = useCallback(() => {
    setExpired(false)
    reset()
    void createLinkCode()
  }, [createLinkCode, reset])

  // One code on mount. The ref guards React strict-mode double effects from
  // burning two single-use codes on every open.
  useEffect(() => {
    if (requestedRef.current) return
    requestedRef.current = true
    void createLinkCode()
  }, [createLinkCode])

  // Tick the countdown. When it runs out, renew silently a few times, then
  // stop and let the reader ask for a fresh code.
  useEffect(() => {
    if (!linkCode) return
    const tick = () => {
      const remaining = secondsUntilExpiry(linkCode.expires_at)
      setSecondsLeft(remaining)
      if (remaining > 0) return
      if (renewalsRef.current < MAX_AUTO_RENEWALS) {
        renewalsRef.current += 1
        requestCode()
      } else {
        setExpired(true)
      }
    }
    tick()
    const timer = window.setInterval(tick, 1000)
    return () => window.clearInterval(timer)
  }, [linkCode, requestCode])

  const code = linkCode?.code ?? null
  const showQr = !!code && !expired
  const qrSize = variant === 'panel' ? 112 : 148

  const qrPlate = (
    <div
      className="shrink-0 rounded-md"
      style={{ background: '#ffffff', border: '1px solid var(--line)', padding: 8 }}
    >
      {showQr ? (
        <QrCode
          value={phoneLinkUrl(code)}
          size={qrSize}
          label="QR code that links this account to the baba app"
        />
      ) : (
        <div
          className="flex items-center justify-center text-center"
          style={{ width: qrSize, height: qrSize, color: '#8a8a8a', fontSize: 11.5 }}
        >
          {isLoading ? 'Loading…' : expired ? 'Code expired' : ' '}
        </div>
      )}
    </div>
  )

  const codeBlock = (
    <div
      className="font-mono tracking-[0.2em]"
      style={{ fontSize: variant === 'panel' ? 24 : 26, fontWeight: 600, color: 'var(--ink)' }}
    >
      {code ? formatLinkCode(code) : '••••-••••'}
    </div>
  )

  const expiryLine = expired ? (
    <button
      type="button"
      onClick={requestCode}
      className="rounded-md transition-all duration-150 hover:bg-[var(--card-2)] hover:border-[var(--line-strong,var(--line))] active:scale-95 cursor-pointer shadow-xs select-none"
      style={{
        height: 30,
        padding: '0 12px',
        fontSize: 12.5,
        fontWeight: 500,
        border: '1px solid var(--line)',
        color: 'var(--ink)',
        background: 'var(--card)',
      }}
    >
      Get a New Code
    </button>
  ) : (
    <span style={{ fontSize: 12, color: 'var(--muted-2)' }}>
      {code ? `Expires in ${formatCountdown(secondsLeft)} · single use` : 'Creating a code…'}
    </span>
  )

  const errorLine = !!error && (
    <p style={{ fontSize: 12, color: 'var(--err)' }}>Could not create a code. Please sign in again.</p>
  )

  const appHint = 'In the app: Settings › Sign in to baba › Have a link code instead?'

  if (variant === 'panel') {
    // One horizontal strip. This sits above the translator someone just came
    // to use, so it earns its space by staying short.
    return (
      <section
        className="relative rounded-md"
        style={{ background: 'var(--card)', border: '1px solid var(--line)', padding: '14px 16px' }}
        aria-labelledby="phone-link-card-title"
      >
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss"
            className="absolute flex items-center justify-center rounded-md transition-colors duration-150 hover:bg-[var(--hover)] cursor-pointer"
            style={{ top: 10, right: 10, height: 26, width: 26, color: 'var(--muted-2)' }}
          >
            <CloseIcon size={11} />
          </button>
        )}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
          {qrPlate}

          <div className="min-w-0 flex-1" style={{ minWidth: 240 }}>
            <div className="flex items-center gap-2">
              <IoPhonePortraitOutline size={15} style={{ color: 'var(--accent-deep)' }} aria-hidden />
              <h2 id="phone-link-card-title" style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>
                Use baba on Your Phone
              </h2>
            </div>
            <p style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 3 }}>
              Your Pro, history and chats follow you. Scan the code, or type it into the app.
            </p>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1" style={{ marginTop: 8 }}>
              {codeBlock}
              {expiryLine}
            </div>
            {errorLine}
            <p style={{ fontSize: 11.5, color: 'var(--muted-2)', marginTop: 6 }}>{appHint}</p>
          </div>

          <div className="shrink-0 flex flex-col gap-2">
            <span style={{ fontSize: 11.5, color: 'var(--muted-2)' }}>No app yet?</span>
            <div dir="ltr">
              <AppStoreLinks placement={placement} showNote={false} />
            </div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start gap-5">
        {qrPlate}
        <div className="min-w-0 flex-1" style={{ minWidth: 220 }}>
          <p style={{ fontSize: 12.5, color: 'var(--muted)' }}>
            Scan this with your phone camera, or enter the code in the app:
          </p>
          <div style={{ margin: '10px 0 6px' }}>{codeBlock}</div>
          {expiryLine}
          {errorLine}
          <p style={{ fontSize: 12, color: 'var(--muted-2)', marginTop: 12 }}>{appHint}</p>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <span style={{ fontSize: 12, color: 'var(--muted-2)' }}>Don't have the app yet?</span>
        <div dir="ltr">
          <AppStoreLinks placement={placement} showNote={false} />
        </div>
      </div>
    </div>
  )
}
